// src/hooks/useFeatureFlags.js
import { useState, useEffect, useMemo, useCallback } from "react";
import { getAuth } from "firebase/auth";
import { getFirestore, doc, getDoc, setDoc } from "firebase/firestore";
import { useAccountUid } from "../contexts/UserContext";

const DEFAULT_FLAGS = {
  showRevenueDashboard: false,
  enablePaymentsHub: true,
  enableSmartRebooking: false,
  enableWaitlist: false,
};

/**
 * Feature flags hook — reads from Firestore + URL param overrides.
 *
 * URL overrides (one-time activation — persisted to Firestore):
 *   ?ff_revenue=1   → enable revenue dashboard
 *   ?ff_payments=1  → enable payments hub
 *
 * Firestore paths:
 *   users/{uid}.featureFlags          (private, read by this hook)
 *   publicGroomers/{uid}.featureFlags (public, read by BookingPage)
 */
export default function useFeatureFlags() {
  const [flags, setFlags] = useState(DEFAULT_FLAGS);
  const [loading, setLoading] = useState(true);
  const [uid, setUid] = useState(null);
  // Owner uid for a delegated team member; equals the signed-in uid otherwise.
  // Feature flags are business-level settings, so reads/writes must key off
  // the account (owner), not the real signed-in identity.
  const accountUid = useAccountUid();

  // Parse URL params once
  const urlOverrides = useMemo(() => {
    if (typeof window === "undefined") return {};
    const params = new URLSearchParams(window.location.search);
    const overrides = {};

    const rev = params.get("ff_revenue");
    if (rev !== null) overrides.showRevenueDashboard = rev === "1" || rev === "true";

    const pay = params.get("ff_payments");
    if (pay !== null) overrides.enablePaymentsHub = pay === "1" || pay === "true";

    const rebook = params.get("ff_rebooking");
    if (rebook !== null) overrides.enableSmartRebooking = rebook === "1" || rebook === "true";

    const waitlist = params.get("ff_waitlist");
    if (waitlist !== null) overrides.enableWaitlist = waitlist === "1" || waitlist === "true";

    return overrides;
  }, []);

  // Persist a flag change to both user doc and public profile
  const persistFlags = useCallback(async (userId, merged) => {
    if (!userId) return;
    const db = getFirestore();
    try {
      await setDoc(doc(db, "users", userId), { featureFlags: merged }, { merge: true });
      await setDoc(doc(db, "publicGroomers", userId), { featureFlags: merged }, { merge: true });
    } catch (err) {
      console.error("Failed to persist feature flags:", err);
    }
  }, []);

  // Public setter — lets the Settings page toggle a flag
  const setFlag = useCallback(async (key, value) => {
    setFlags((prev) => {
      const next = { ...prev, [key]: value };
      // fire-and-forget persistence
      persistFlags(uid, next);
      return next;
    });
  }, [uid, persistFlags]);

  useEffect(() => {
    const auth = getAuth();
    const db = getFirestore();

    const unsubscribe = auth.onAuthStateChanged(async (firebaseUser) => {
      if (!firebaseUser) {
        setUid(null);
        setFlags(DEFAULT_FLAGS);
        setLoading(false);
        return;
      }

      // Wait for the account uid to resolve (accountUid falls back to the
      // signed-in uid for everyone without a delegated-access claim).
      if (!accountUid) return;

      setUid(accountUid);

      try {
        const userDoc = await getDoc(doc(db, "users", accountUid));
        const firestoreFlags = userDoc.exists() ? userDoc.data().featureFlags || {} : {};

        // Merge: URL overrides > Firestore > defaults
        const merged = {
          showRevenueDashboard: urlOverrides.showRevenueDashboard ?? firestoreFlags.showRevenueDashboard ?? false,
          enablePaymentsHub: urlOverrides.enablePaymentsHub ?? firestoreFlags.enablePaymentsHub ?? false,
          enableSmartRebooking: urlOverrides.enableSmartRebooking ?? firestoreFlags.enableSmartRebooking ?? false,
          enableWaitlist: urlOverrides.enableWaitlist ?? firestoreFlags.enableWaitlist ?? false,
        };

        setFlags(merged);

        // If URL overrides activated any flag, persist to Firestore so it sticks
        if (Object.keys(urlOverrides).length > 0) {
          const needsPersist = Object.entries(urlOverrides).some(
            ([k, v]) => v === true && !firestoreFlags[k]
          );
          if (needsPersist) {
            await persistFlags(accountUid, merged);
            // Clean up the URL so the param doesn't linger
            const url = new URL(window.location.href);
            url.searchParams.delete("ff_revenue");
            url.searchParams.delete("ff_payments");
            url.searchParams.delete("ff_rebooking");
            url.searchParams.delete("ff_waitlist");
            window.history.replaceState({}, "", url.toString());
          }
        }
      } catch (error) {
        console.error("Error loading feature flags:", error);
        setFlags({
          showRevenueDashboard: urlOverrides.showRevenueDashboard ?? false,
          enablePaymentsHub: urlOverrides.enablePaymentsHub ?? false,
          enableSmartRebooking: urlOverrides.enableSmartRebooking ?? false,
          enableWaitlist: urlOverrides.enableWaitlist ?? false,
        });
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, [urlOverrides, persistFlags, accountUid]);

  return { flags, loading, setFlag };
}

